import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Truck, Radio, Shield, Mail, Lock, ArrowRight } from "lucide-react";

// Coloque a imagem em public/martiterra-logo-sidebar.png.
// Caso o arquivo tenha outro nome, altere somente esta constante.
const MARTITERRA_LOGO = "/martiterra-logo-sidebar.png";

export default function Login() {
  const { user, login, error } = useAuth();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) nav("/");
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const ok = await login(identifier.trim(), password);
      if (ok) nav("/");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2 bg-[#090D11] text-[#F4F7F9]">
      {/* WORKFLEET — painel institucional */}
      <section className="relative hidden min-h-screen flex-col justify-between overflow-hidden border-r border-[#18332C] bg-[#071311] p-10 lg:flex xl:p-14">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
          background: "radial-gradient(ellipse at 95% 5%, rgba(9,130,79,.13), transparent 45%), radial-gradient(ellipse at 0% 100%, rgba(7,94,62,.12), transparent 48%)"
        }} />
        <div aria-hidden="true" className="pointer-events-none absolute -right-40 top-[-15%] h-[85%] w-[55%] rotate-[34deg] border-x border-emerald-700/10" />

        <header className="relative z-10 flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#2BE76F]">
            <Truck className="h-6 w-6 text-[#071311]" />
          </div>
          <div>
            <div className="font-display text-2xl font-extrabold uppercase leading-none tracking-tight">WorkFleet</div>
            <div className="mt-1 text-[10px] uppercase tracking-[0.15em] text-[#B9C6C4] sm:text-xs">
              Telemática <span className="text-[#2BE76F]">•</span> Manutenção <span className="text-[#2BE76F]">•</span> Inspeções
            </div>
          </div>
        </header>

        <div className="relative z-10 max-w-[490px] space-y-6">
          <h1 className="font-display text-4xl font-extrabold uppercase leading-[1.05] tracking-tight xl:text-5xl">
            Inspeções de frota <span className="text-[#2BE76F]">disparadas</span> pela telemetria.
          </h1>
          <p className="max-w-[460px] text-sm leading-7 text-[#B6C1C0] xl:text-base">
            Receba gatilhos do Fleetrun/Wialon, distribua checklists inteligentes para mecânicos em campo e feche o ciclo com orçamentos e aprovações.
          </p>
          <div className="grid grid-cols-3 gap-3">
            {[
              { icon: Radio, title: "Gatilho", value: "Wialon Live" },
              { icon: Truck, title: "Frota", value: "100% rastreada" },
              { icon: Shield, title: "Auditoria", value: "Fotos + logs" },
            ].map(({ icon: Icon, title, value }) => (
              <Card key={title} className="min-w-0 rounded-xl border-[#24413A] bg-[#091513]/80 p-3 text-[#F4F7F9] shadow-none xl:p-4">
                <Icon className="mb-3 h-5 w-5 text-[#2BE76F]" />
                <div className="text-[10px] font-semibold uppercase tracking-widest text-[#9AAEAA]">{title}</div>
                <div className="mt-1 text-xs font-semibold xl:text-sm">{value}</div>
              </Card>
            ))}
          </div>
        </div>

        <footer className="relative z-10 text-xs text-[#8EA39F]">© 2026 WorkFleet • Pt-BR</footer>
      </section>

      {/* MARTITERRA — acesso interno */}
      <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#101112] px-6 py-12 sm:px-10">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
          background: "radial-gradient(ellipse at 100% 100%, rgba(255,198,41,.09), transparent 42%), radial-gradient(ellipse at 0% 0%, rgba(255,198,41,.025), transparent 40%)"
        }} />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-56 -right-32 h-[480px] w-[340px] rotate-[38deg] rounded-[70px] border border-[#FFC629]/10" />
        <div className="relative z-10 w-full max-w-[420px]">
          <div className="mb-12 flex justify-center sm:mb-14">
            <img
              src={MARTITERRA_LOGO}
              alt="Martiterra Engenharia"
              className="h-auto w-full max-w-[350px] object-contain"
            />
          </div>

          <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight">Acesso à plataforma</h2>
          <p className="mb-7 mt-2 text-sm leading-6 text-[#A9AFB5]">
            Entre com sua conta para gerenciar inspeções e manutenções.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="identifier" className="text-sm font-medium text-[#F4F7F9]">Nome ou Email</Label>
              <div className="relative">
                <Mail aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8B969F]" />
                <Input id="identifier" data-testid="login-identifier" type="text" required autoComplete="username"
                  value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="Seu nome ou email"
                  className="h-12 border-[#343A40] bg-[#0D1114] pl-10 text-[#F4F7F9] placeholder:text-[#8B969F] focus-visible:ring-[#FFC629]" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-[#F4F7F9]">Senha</Label>
              <div className="relative">
                <Lock aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8B969F]" />
                <Input id="password" data-testid="login-password" type="password" required autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                  className="h-12 border-[#343A40] bg-[#0D1114] pl-10 text-[#F4F7F9] placeholder:text-[#8B969F] focus-visible:ring-[#FFC629]" />
              </div>
            </div>
            {error && <div role="alert" className="text-sm text-red-400" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="h-12 w-full gap-2 bg-[#FFC629] font-semibold uppercase tracking-wide text-[#171717] hover:bg-[#FFD65A] focus-visible:ring-[#FFC629] disabled:opacity-60">
              {loading ? "Entrando..." : <>Entrar <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </form>
          <p className="mt-10 text-center text-[11px] text-[#777E83] lg:hidden">Plataforma WorkFleet • Martiterra Engenharia</p>
        </div>
      </main>
    </div>
  );
}
